import { api } from './client';

/**
 * Endpoints de la configuración de la INSTALACIÓN (des-hardcodeo multi-tenant).
 * Prefijo: /api/installation-config. La config completa es ADMIN; la vista `/crm`
 * (opciones de la ficha del lead) es para cualquier usuario autenticado.
 */

export interface CatalogModel {
  name: string;
  price: number;
  note?: string;
  /** Descripción de venta (1-3 frases). Va al PDF y al agente como referencia. */
  descripcion?: string;
  aliases?: string[];
  imagenes: string[];
}

export interface CrmEditableField {
  key: string;
  label: string;
  placeholder: string;
}

/** Centro de operación (base física): referencia en Tracking. */
export interface OperationCenter {
  nombre: string;
  lat: number;
  lng: number;
  /** Radio en km que define "estar en la base". */
  radioKm: number;
}

export interface CrmConfig {
  leadStatusLabels: Record<string, string>;
  editableFields: CrmEditableField[];
  zonas: string[];
  tiposServicio: string[];
  /** Centros de operación (bases). Vacío = sin bases definidas. */
  centros: OperationCenter[];
}

export interface MessagesConfig {
  aiFallback: string;
  survey: string;
  /** Encuesta de un caso resuelto a distancia (sin visita). Vars: {{cliente}}. */
  surveyRemote: string;
  /** Aviso al técnico: se le asignó un trabajo. Vars: {{tecnico}} {{cliente}} {{zona}} {{cita}} {{direccion}} {{descripcion}}. */
  trabajoAsignadoTecnico: string;
  slaOverdueOwner: string;
  slaOverdueTechnician: string;
  jobNotCompletedOwner: string;
  appointmentReminder: string;
  technicianArrived: string;
  surveyBadAlertOwner: string;
  /** Respuesta automática al cliente por encuesta POSITIVA. Vars: {{cliente}}. */
  surveyThanksGood: string;
  /** Respuesta automática al cliente por QUEJA. Vars: {{cliente}}. */
  surveyThanksBad: string;
  /** Acuse cordial para respuesta neutral o no clasificable. Vars: {{cliente}}. */
  surveyThanksNeutral: string;
  /** Aviso al dueño cuando un lead escala a CALIENTE. Vars: {{cliente}} {{telefono}} {{modelo}} {{zona}}. */
  hotLeadOwner: string;
  /** Aviso al dueño al ponchar LLEGADA. Vars: {{tecnico}} {{cliente}} {{direccion}} {{zona}} {{hora}}. */
  tecnicoLlegoOwner: string;
  /** Aviso al dueño al ponchar TERMINÉ. Vars: {{tecnico}} {{cliente}} {{direccion}} {{zona}} {{hora}}. */
  tecnicoTerminoOwner: string;
  /** Aviso interno: el técnico reporta que el cliente NO llegó. Vars: {{tecnico}} {{cliente}} {{descripcion}} {{zona}}. */
  noShowOwner: string;
  /** BAJAS · Confirmación al contacto que pidió no recibir más mensajes. Vars: {{cliente}}. */
  optOutConfirmation: string;
  /** BAJAS · Confirmación de re-alta explícita ("ALTA"). Vars: {{cliente}}. */
  optInConfirmation: string;
  /** Aviso al dueño cuando el guard de gasto corta al agente. Vars: {{alcance}} {{gastado}} {{tope}}. */
  spendCapReachedOwner: string;
  /**
   * El mismo CLIENTE volvió a abrir una garantía teniendo equipos en cobertura.
   * Vars: {{cliente}} {{telefono}} {{cantidad}} {{ventana}} {{descripcion}}.
   */
  garantiaReincidenteOwner: string;
  /**
   * El mismo ARTÍCULO volvió a dar problema dentro de su período de cobertura.
   * Vars: {{cliente}} {{telefono}} {{modelo}} {{cantidad}} {{ventana}} {{descripcion}}.
   */
  garantiaReincidenteInstalacionOwner: string;
  /** Encabezado del resumen diario de trabajos. Vars: {{fecha}}. */
  resumenDiarioTrabajos: string;
  /** El técnico cerró lejos del destino. Vars: {{tecnico}} {{cliente}} {{distancia}} {{direccion}}. */
  cierreFueraDeRadioOwner: string;
  /**
   * Pendiente del vendedor, al teléfono del vendedor.
   * Vars: {{codigo}} {{descripcion}} {{cliente}} {{telefono}} {{quote}}.
   */
  pendienteVendedor: string;
  /**
   * Pendiente de VENTA. Los signos de peso al principio son el marcador visual
   * que pidió el cliente. Mismas variables.
   */
  pendienteVentaVendedor: string;
}

/** Reglas de ENVÍO del agente (cómo sale la respuesta por WhatsApp). */
export interface EnvioConfig {
  /**
   * Tope de mensajes ("burbujas") que el agente manda por turno. Cada burbuja
   * es un sobre cifrado extra hacia el mismo contacto: cuantos más, más se
   * degrada la sesión de WhatsApp del cliente.
   */
  maxBurbujas: number;
  /**
   * ¿Mandar el doble check azul cuando el asistente responde? Es señal
   * anti-ban, pero también apaga las notificaciones del móvil del equipo.
   */
  acusesLectura: boolean;
  /**
   * Guarda de salida: términos internos que nunca deben llegarle al cliente.
   * Sanea la oración contaminada; no mata el turno entero.
   */
  guardaSalida: { activa: boolean; terminos: string[]; patrones: string[] };
  /** Canal explícito hacia el cliente (`responder_al_cliente`). Default APAGADO. */
  canalRespuesta: boolean;
  /** No invocar al asistente si el turno del cliente no trae texto útil. */
  turnoIlegible: { activo: boolean; minProporcion: number };
  /** Tope de tamaño del video entrante, en MB. */
  maxVideoMb: number;
}

/** Descripciones (editables) de lo que el cliente manda y no es texto. */
export interface EntradaNoTextoConfig {
  imagen: string;
  video: string;
  audio: string;
  notaVoz: string;
  sticker: string;
  documento: string;
  /** Variable disponible: {{nombre}}. */
  documentoConNombre: string;
  ubicacion: string;
  contacto: string;
  multimedia: string;
  noDescifrado: string;
}

/** Reglas de CONVERSACIÓN: qué VE el asistente (no qué dice). */
export interface ConversacionConfig {
  operador: { distinguir: boolean; etiqueta: string };
  entradaNoTexto: EntradaNoTextoConfig;
}

/** Un texto que delata que el contacto vino de publicidad. */
export interface IndicadorPublicidad {
  id: string;
  texto: string;
  activo: boolean;
}

/** Tipo de caso definido por el dueño y a quién se le rutea. */
export interface TipoCaso {
  id: string;
  nombre: string;
  /** Ids del directorio de números del ruteo de avisos. */
  numeroIds: string[];
}

/** Config del AGENTE INSPECTOR: decide si la Asesora entra. */
export interface InspectorConfig {
  activo: boolean;
  prompt: string;
  modelo: string | null;
  capas: {
    senalAnuncio: boolean;
    historial: boolean;
    indicadores: boolean;
    intencion: boolean;
  };
  indicadores: IndicadorPublicidad[];
  cortesia: { texto: string };
  tiposCaso: TipoCaso[];
  /** Vars: {{cliente}} {{telefono}} {{tipo}} {{mensaje}}. */
  avisoDestinatario: string;
  porDefecto: 'elegible' | 'noElegible';
  noElegible: { enviarCortesia: boolean; notificarDestinatario: boolean };
}

export interface AgentPersona {
  name: string;
  description: string;
  model: string;
  maxTokens: number;
  temperature: number;
  systemPrompt: string;
}

/**
 * S-01 · Un modelo de artículo con su período de garantía, en MESES. El
 * catálogo lo edita el dueño desde Configuración → Trabajos.
 */
export interface ModeloCobertura {
  id: string;
  nombre: string;
  mesesCobertura: number;
}

/** S-01 · Una forma de pago del cierre. Se desactiva sin borrarla. */
export interface FormaPagoOpcion {
  id: string;
  nombre: string;
  activa: boolean;
}

/**
 * S-02 · Un nivel de resolución de garantía. `codigo` (N1…N4) es lo que se
 * guarda en el trabajo; `nombre` es lo que se lee en pantalla.
 */
export interface NivelResolucion {
  id: string;
  codigo: string;
  nombre: string;
  activo: boolean;
}

/** S-02 · Una causa raíz de garantía. */
export interface CausaRaiz {
  id: string;
  nombre: string;
  activo: boolean;
}

/** Catálogos y reglas del módulo Trabajos (tanda EMT-CNV-2026-01). */
export interface TrabajosConfig {
  modelos: ModeloCobertura[];
  formasPago: FormaPagoOpcion[];
  nivelesResolucion: NivelResolucion[];
  causasRaiz: CausaRaiz[];
  /** S-07 · Motivos tipificados de "no completado". */
  motivosNoCompletado: CausaRaiz[];
  /** S-09 · Horas para atender una garantía. Default 24 (número del equipo). */
  garantiaSlaHoras: number;
  /** Override de la ventana de reincidencia en días. null = cobertura del modelo. */
  reincidenciaVentanaDias: number | null;
  /** S-05 · Radio, GPS obligatorio y clave del servicio de mapas. */
  geocerca: GeocercaConfig;
  /** S-14 · Resumen diario que reemplaza los avisos uno por uno. */
  resumenDiario: ResumenDiarioConfig;
  /** PUNTO 6 · Cuestionario de garantías, cargado y apagado. */
  encuestaGarantia: EncuestaGarantiaConfig;
}

/** S-14 · Qué bloques entran en el resumen diario. */
export interface ResumenIncluye {
  completados: boolean;
  noCompletados: boolean;
  vencidos: boolean;
  cierresFueraDeRadio: boolean;
  garantiasAbiertas: boolean;
  reincidencias: boolean;
  montoVendido: boolean;
  /** Conversaciones que esperan a una persona (quejas, pases a humano). */
  esperandoPersona: boolean;
}

/** S-14 · Hora, canal y contenido del resumen diario. */
export interface ResumenDiarioConfig {
  activo: boolean;
  hora: string;
  canal: 'whatsapp' | 'email';
  incluye: ResumenIncluye;
}

/** S-05 · Reglas de ubicación. La clave la carga el dueño desde el panel. */
export interface GeocercaConfig {
  radioMetrosDefault: number;
  exigirGpsAlCerrar: boolean;
  mapsApiKey: string | null;
}

/** Un término del negocio, con lo que significa acá adentro. */
export interface TerminoGlosario {
  termino: string;
  significa: string;
  /** Cómo se llama el campo en la base, si aplica. */
  enLaBase?: string;
}

/** Algo que NO hay que decir, con su reemplazo. */
export interface TerminoAEvitar {
  no: string;
  si: string;
}

/**
 * BLOQUE 2 · El vocabulario del negocio. No es documentación: se inyecta en el
 * prompt del copiloto para que hable con las palabras del cliente.
 */
export interface Glosario {
  terminos: TerminoGlosario[];
  evitar: TerminoAEvitar[];
}

/**
 * B1 · El COPILOTO interno.
 *
 * `motor` no está: no hay opción que elegir. El puente CLI omite las
 * herramientas y el copiloto ES herramientas.
 */
export interface CopilotoConfig {
  activo: boolean;
  /**
   * Con qué se le habla al modelo.
   *
   *   'api' — la clave de Anthropic del negocio. Se paga por token.
   *   'cli' — el puente al VPS de Silva Notion, sobre su suscripción.
   *
   * Elegir 'cli' no alcanza para usarlo: el server exige instalación interna y
   * cae a 'api' si no lo es. Por eso el selector solo se muestra donde el
   * permiso existe — ofrecer una opción que no hace nada es peor que no
   * ofrecerla.
   */
  motor: 'api' | 'cli';
  modelo: string;
  nombreAgente: string;
  nombreDueno: string;
  descripcionNegocio: string;
  fraseConfirmacion: string;
  minutosAprobacion: number;
  umbralMasivo: number;
  maxSegundosNota: number;
  datosRestringidos: string[];
  audioSaliente: boolean;
  /** B7 · Clave del proveedor de voz. La carga el dueño; nace vacía. */
  vozApiKey: string;
  /** Qué voz usar, del catálogo del proveedor. Nace vacía. */
  vozId: string;
}

/**
 * Pendientes del vendedor: los compromisos con clientes que detecta la IA.
 *
 * El módulo existía entero en el server y no se podía encender: el PUT recortaba
 * esta clave y no había pantalla.
 */
export interface PendientesConfig {
  /** Avisarle al vendedor. Apagado: detecta y guarda, pero no escribe a nadie. */
  activo: boolean;
  /** Franja del día en la que puede molestar. `hasta` es exclusivo. */
  ventana: { desde: string; hasta: string };
  /** Escalación por llamada. Independiente del interruptor de avisos. */
  llamadas: { activo: boolean; minutosEscalacion: number; minutosEscalacionVenta: number };
  maxChatsPorCorrida: number;
  modelo: string;
  horasMargenVencimiento: number;
  soloChatsActivosDias: number;
}

/** Config completa de la instalación (vista/edición de ADMIN). */
/** Una de las tiendas de Osito (A6). Espejo de `services/osito-config.ts`. */
export interface SucursalConfig {
  id: string;
  nombre: string;
  /** Nace VACÍA: la carga el dueño. El agente no nombra una dirección vacía. */
  direccion: string;
  telefono: string;
  lat: number | null;
  lng: number | null;
  esSedeOperacion: boolean;
  /** false = la sucursal solo se traquea; el agente no responde por su canal. */
  agenteResponde: boolean;
  activa: boolean;
}

/** Horario de una tienda. `dia` 0 = domingo. `null` = ese día no abre. */
export interface DiaHorario {
  dia: number;
  abre: string | null;
  cierra: string | null;
}

/** Qué partes del producto ve este negocio. Espejo de `osito-config.ts`. */
export interface ModulosActivos {
  /** Trabajos, garantías, técnicos, despachos, desempeño. */
  instalaciones: boolean;
  campanas: boolean;
  descartes: boolean;
  entrenamiento: boolean;
}

/** Reglas de negocio de Osito (A6). */
export interface OsitoConfig {
  sucursales: SucursalConfig[];
  modulos: ModulosActivos;
  /** Horario de la TIENDA FÍSICA. */
  horario: DiaHorario[];
  /** Horario en que hay PERSONAS contestando el WhatsApp. Son dos cosas. */
  horarioAtencion: DiaHorario[];
  /** Lo que dice el agente fuera de horario. Nunca promete hora de entrega. */
  fraseFueraDeHorario: string;
  /** Minutos que tiene el vendedor para tomar el chat antes de reasignar. */
  minutosParaTomarElChat: number;
  /** Tope de reintentos al cliente por asunto. */
  maxIntentosPorAsunto: number;
  /** Quién atiende a los mayoristas. Vacío = caen al reparto normal. */
  vendedoraMayoreoUserId: string;
  familiasProducto: string[];
}

export interface InstallationConfig {
  key: string;
  businessName: string;
  agent: AgentPersona;
  catalog: CatalogModel[];
  notionDatabaseId: string | null;
  crm: CrmConfig;
  messages: MessagesConfig;
  /** Reglas de envío del agente. */
  envio: EnvioConfig;
  /** Reglas de armado del turno (qué ve el asistente). */
  conversacion: ConversacionConfig;
  /** Agente Inspector: decide si la Asesora entra en una conversación nueva. */
  inspector: InspectorConfig;
  /** Catálogos y reglas del módulo Trabajos. */
  trabajos: TrabajosConfig;
  /** El vocabulario del negocio (BLOQUE 2). */
  glosario: Glosario;
  /** El copiloto interno (B1). Nace apagado. */
  copiloto: CopilotoConfig;
  /** Pendientes del vendedor. Nace sin avisar a nadie. */
  pendientes: PendientesConfig;
  /** Reglas de negocio de Osito: tiendas, horario, reparto. */
  osito: OsitoConfig;
  slaHours: number;
  currencyPrefix: string;
  /** Logo del negocio (clave de storage) — se sube aparte, no viaja en el PUT. */
  logoKey: string | null;
  /** PDF del catálogo pre-generado (clave de storage), o null si no existe aún. */
  catalogPdfKey: string | null;
  /** Instalación interna de Silva Notion. SOLO lectura: el PUT no lo acepta. */
  isInternal: boolean;
}

/** Vista para la ficha del lead (cualquier usuario autenticado). */
export interface CrmView {
  businessName: string;
  currencyPrefix: string;
  crm: CrmConfig;
  /** Catálogos de Trabajos: el técnico los necesita para cerrar en sitio. */
  trabajos?: TrabajosConfig;
  /** Las tiendas y el horario: la ficha los necesita para "a qué tienda va". */
  osito?: OsitoConfig;
  catalog: { name: string; price: number }[];
  /** Instalación interna de Silva Notion (habilita el selector de motor). */
  isInternal?: boolean;
}

export const getInstallationConfig = () => api.get<InstallationConfig>('/api/installation-config');

/**
 * Lo que viaja en el PUT: la config completa MENOS lo que no se edita a mano
 * (la clave de la instalación, el logo, el PDF y la marca de instalación
 * interna). Tiene nombre propio porque el guardado por pestaña arma este mismo
 * objeto mezclando lo que ya hay en el servidor con lo que tocó UNA pestaña.
 */
export type InstallationConfigPayload = Omit<
  InstallationConfig,
  'key' | 'logoKey' | 'catalogPdfKey' | 'isInternal' | 'osito'
> & {
  /**
   * Opcional, igual que en el zod del servidor: una pantalla que no edita las
   * tiendas no tiene por qué mandarlas, y el coerce del API cae a los defaults.
   * Lo que NO puede pasar es que viaje recortado desde la pestaña Tiendas — de
   * eso se encarga `CLAVES_DE_PESTANA`.
   */
  osito?: OsitoConfig;
};

export const saveInstallationConfig = (config: InstallationConfigPayload) =>
  api.put<InstallationConfig>('/api/installation-config', config);

/** PUNTO 5 · Consumo del servicio de mapas del mes en curso. */
export interface ConsumoMapas {
  mes: string;
  tieneClave: boolean;
  consultasReales: number;
  consultasEstimadas: number;
}

export const getConsumoMapas = () =>
  api.get<ConsumoMapas>('/api/installation-config/consumo-mapas');

/** PUNTO 6 · Mensajes automáticos salientes del mes en curso. */
export interface SalienteDelMes {
  mes: string;
  mensajes: number;
}

export const getSalienteDelMes = () =>
  api.get<SalienteDelMes>('/api/installation-config/saliente-mes');

/** PUNTO 6 · Una pregunta del cuestionario de garantías. */
export interface PreguntaEncuesta {
  id: string;
  texto: string;
  tipo: 'opciones' | 'escala' | 'texto';
  opciones: string[];
}

/** PUNTO 6 · Cuestionario de garantías (cargado y apagado). */
export interface EncuestaGarantiaConfig {
  activa: boolean;
  preguntas: PreguntaEncuesta[];
}

export const getCrmView = () => api.get<CrmView>('/api/installation-config/crm');

/** Sube el logo del negocio (multipart). Devuelve la clave/URL guardadas. */
export const uploadLogo = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post<{ key: string; url: string }>('/api/installation-config/logo', form);
};

/** Sube la foto de un producto del catálogo. Devuelve clave/URL para agregarla al modelo. */
export const uploadCatalogImage = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post<{ key: string; url: string }>('/api/installation-config/catalog-image', form);
};

/** Regenera el PDF del catálogo (tras subir fotos nuevas, p. ej.). */
export const regenerateCatalogPdf = () =>
  api.post<{ catalogPdfKey: string; url: string }>('/api/installation-config/regenerate-pdf');

/**
 * Verifica en el servidor qué claves de imagen NO tienen archivo en el storage
 * (claves "fantasma"). Base de la acción "quitar fotos rotas" de una fila.
 */
export const checkCatalogImages = (keys: string[]) =>
  api.post<{ missing: string[] }>('/api/installation-config/catalog-images/check', { keys });
